// بيانات المستخدم الحالي + صلاحيات "البوكسات" اللي يمنحها المدير من واجهة
// "صلاحية المستخدمين". نفس مفاتيح البوكسات موجودة بالباك اند
// (artika-backend/config/permissions.js).
//
// username بـlocalStorage = الهوية الثابتة للحساب (مثلاً "karina@123" أو
// "user_xxx" للحسابات الجديدة) - ما تتغيّر لو المدير غيّر اسم الدخول أو الاسم
// الظاهر، فكل الكود المربوط بالهوية يبقى شغال.

// البوكسات بالترتيب المعتمد للوحة الموظفة. path = الصفحة، href = رابط خارجي.
// "مهامي" لحسابات البوكسات صفحتها /my-tasks (باسم الموظفة الظاهر).
export const PERMISSION_BOXES = [
  { key: "canvasCost", label: "سعر اللوحة", icon: "💰", path: "/canvas-cost" },
  { key: "createInvoice", label: "انشاء فاتورة", icon: "🧾", path: "/invoice" },
  { key: "invoices", label: "الفواتير", icon: "📑", path: "/invoices" },
  { key: "proposals", label: "عروض الأسعار", icon: "📄", path: "/proposals" },
  { key: "directSale", label: "بيع مباشر", icon: "🗂️", path: "/archived-direct" },
  { key: "drawingVideos", label: "فديوات الرسم", icon: "🎬", path: "/story-videos" },
  { key: "siteVisit", label: "زيارة الموقع", icon: "📍", path: "/site-visit" },
  { key: "leads", label: "الزبائن المحتملين", icon: "📌", path: "/leads" },
  { key: "myTasks", label: "مهامي", icon: "📋", path: "/my-tasks" },
  { key: "todo", label: "To - Do", icon: "✅", path: "/todo" },
  { key: "leaveRequest", label: "طلب إجازة", icon: "📝", path: "/leave-request" },
  { key: "materialRequest", label: "طلب مواد", icon: "🧰", path: "/material-request" },
  { key: "ordersAdmin", label: "ادارة الطلبات", icon: "🗂️", href: "https://admin-artika.netlify.app/login" },
];

// تقسيم البوكسات بواجهة صلاحية المستخدمين (للعرض فقط - الترتيب أعلاه يبقى
// هو ترتيب البوكسات بلوحة الموظفة)
export const PERMISSION_BOX_GROUPS = [
  { title: "أساسية (ستاندرد لكل الموظفين)", keys: ["leaveRequest", "materialRequest", "myTasks", "todo"] },
  {
    title: "المبيعات والفواتير",
    keys: ["canvasCost", "createInvoice", "invoices", "proposals", "directSale", "leads", "siteVisit"],
  },
  { title: "الإنتاج والرسم", keys: ["drawingVideos"] },
  { title: "الإدارة", keys: ["ordersAdmin"] },
];

// المسارات اللي يفتحها كل بوكس (بما فيها الصفحات الفرعية مثل عرض/تعديل فاتورة)
const PERMISSION_PATHS = {
  canvasCost: [/^\/canvas-cost$/],
  createInvoice: [/^\/invoice$/, /^\/invoice\/edit\/[^/]+$/],
  invoices: [
    /^\/invoices$/,
    /^\/invoice\/[^/]+$/,
    /^\/invoice\/edit\/[^/]+$/,
    /^\/invoice\/version\/edit\/[^/]+\/[^/]+$/,
    /^\/invoice-stats$/,
  ],
  proposals: [/^\/proposals$/, /^\/proposals\/[^/]+$/],
  directSale: [/^\/archived-direct$/],
  drawingVideos: [/^\/story-videos$/],
  siteVisit: [/^\/site-visit$/],
  leads: [/^\/leads$/],
  myTasks: [/^\/my-tasks$/],
  todo: [/^\/todo$/],
  leaveRequest: [/^\/leave-request$/],
  materialRequest: [/^\/material-request$/],
};

const PROFILE_KEYS = ["displayName", "loginName", "permissions", "dashboardType", "hiddenBuiltIns"];

const readJson = (key, fallback) => {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return value ?? fallback;
  } catch {
    return fallback;
  }
};

export const getCurrentProfile = () => ({
  username: localStorage.getItem("username"),
  displayName: localStorage.getItem("displayName") || "",
  loginName: localStorage.getItem("loginName") || "",
  permissions: readJson("permissions", []),
  dashboardType: localStorage.getItem("dashboardType") || "legacy",
});

// يخزّن بيانات الدخول/التحديث (نفس شكل رد /auth/login و /me/profile)
export const saveProfile = (data) => {
  if (data.username) localStorage.setItem("username", data.username);
  if (data.displayName !== undefined) localStorage.setItem("displayName", data.displayName || "");
  if (data.loginName !== undefined) localStorage.setItem("loginName", data.loginName || "");
  if (data.dashboardType !== undefined) localStorage.setItem("dashboardType", data.dashboardType || "legacy");
  if (data.permissions !== undefined) localStorage.setItem("permissions", JSON.stringify(data.permissions || []));
  // أزرار لوحته الثابتة اللي المدير شالها (utils/accountBuiltIns.js)
  if (data.hiddenBuiltIns !== undefined) {
    localStorage.setItem("hiddenBuiltIns", JSON.stringify(data.hiddenBuiltIns || []));
  }
  if (data.grantedDashboards !== undefined) {
    localStorage.setItem("grantedDashboards", JSON.stringify(data.grantedDashboards || []));
  }
};

export const clearProfile = () => {
  PROFILE_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const isBoxAccount = () => localStorage.getItem("dashboardType") === "boxes";

export const hasPermission = (key) => getCurrentProfile().permissions.includes(key);

// هل البوكسات الممنوحة للمستخدم الحالي تفتح هذا المسار؟
export const permissionAllowsPath = (pathname) =>
  getCurrentProfile().permissions.some((key) =>
    (PERMISSION_PATHS[key] || []).some((re) => re.test(pathname))
  );

// البوكسات الممنوحة بالترتيب المعتمد
export const getGrantedBoxes = (permissions) =>
  PERMISSION_BOXES.filter((box) => (permissions || []).includes(box.key));

// اسم الموظفة الحالية للحسابات الجديدة (البوكسات) - الحسابات القديمة تبقى
// على خرائط الأسماء الثابتة بكل صفحة
export const getBoxEmployeeName = () => (isBoxAccount() ? getCurrentProfile().displayName : "");
